import {
  faPlus,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { ColumnDef, Cell } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";

import { startsWithInternalPrefix } from "src/api/constants";
import { ApiKeyActions } from "src/apiKeys/ApiKeyActions";
import { CreateApiKeyModal } from "src/apiKeys/CreateApiKeyModal";
import { DeleteApiKeyModal } from "src/apiKeys/DeleteApiKeyModal";
import { EditApiKeyModal } from "src/apiKeys/EditApiKeyModal";
import { KeyType } from "src/apiKeys/KeyType";
import { ShowApiKeyModal } from "src/apiKeys/ShowApiKeyModal";
import { useWorkspaceApiKeys } from "src/apiKeys/api/queries";
import EmptyCube from "src/assets/empty-state-cube.svg?react";
import { Card } from "src/base-components/Card";
import { Pill } from "src/base-components/Pill";
import { TableComp as Table } from "src/base-components/Table";
import {
  ApiKeyType,
  WorkspaceServiceAccount,
  WorkspaceServiceAccountWithKey,
} from "src/clients/taktile-api";
import { RawCell } from "src/dataTable/RawCell";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { SubHeader } from "src/flow/SubHeader";
import { useCapabilities } from "src/hooks/useCapabilities";
import { ColumnHeader } from "src/jobs/common/TableElements";
import { DashboardContent } from "src/layout/DashboardContent";
import { dateFromNow, formatDate } from "src/utils/datetime";
import { errorMessage } from "src/utils/stringUtils";

const renderDateInfo = <T extends WorkspaceServiceAccount>(
  cell: Cell<T, string>,
) => {
  const isoDate = cell.getValue();
  const fromNow = isoDate ? dateFromNow(isoDate) : "Never";
  const detailDate = isoDate
    ? formatDate(isoDate, "dd MMM yy HH:mm:ss")
    : "Never used";

  return (
    <Tooltip placement="top" title={detailDate}>
      <RawCell<ApiKeyType>
        cell={<span>{fromNow}</span>}
        classNameOverrides="min-h-[48px]"
      />
    </Tooltip>
  );
};

const renderNoApiKeys = () => (
  <div className="mx-auto flex h-[19.875rem] w-[57.25rem] flex-row items-center justify-center">
    <div className="flex flex-row">
      <EmptyCube className="mr-5" />
      <div className="text-gray-800">
        <div className="pt-1.5 font-inter-semibold-14px">
          No API keys created
        </div>
        <div className="font-inter-normal-13px">
          Please create an API key to integrate <br /> Taktile with your system.
        </div>
      </div>
    </div>
  </div>
);

const Header: React.FC<{ header?: string }> = ({ header }) => (
  <ColumnHeader>{header}</ColumnHeader>
);

type Props = {
  organizationId: string;
  workspaceId: string;
};

type CallbackWithApiKey = (apiKey: WorkspaceServiceAccount) => void;

const buildColumns = (
  onEdit: CallbackWithApiKey,
  onDelete: CallbackWithApiKey,
): ColumnDef<WorkspaceServiceAccount>[] => {
  const columns: ColumnDef<WorkspaceServiceAccount>[] = [
    {
      id: "keyName",
      header: () => <Header header="Key name" />,
      accessorFn: (row) => row.name,
      cell: ({ cell }) => {
        const isInternalKey = startsWithInternalPrefix(cell.row.original.name);
        const wrapper = (
          <span className="inline-flex items-center gap-2">
            {isInternalKey && (
              <Pill size="sm" variant="red">
                <Pill.Text>Internal</Pill.Text>
              </Pill>
            )}
            {cell.renderValue<string>()}
          </span>
        );
        return (
          <RawCell<WorkspaceServiceAccount>
            cell={wrapper}
            classNameOverrides="min-h-[48px] max-w-0"
          />
        );
      },
    },
    {
      id: "keyTypes",
      header: () => <Header header="Key permissions" />,
      accessorFn: (row) => row.key_types,
      cell: ({ cell }) => <KeyType value={cell.getValue() as ApiKeyType[]} />,
    },
    {
      id: "createdBy",
      header: () => <Header header="Created by" />,
      accessorFn: (row) => row.sponsored_by_username ?? "",
      cell: ({ cell }) => (
        <RawCell<WorkspaceServiceAccount>
          cell={cell}
          classNameOverrides="min-h-[48px]"
        />
      ),
    },
    {
      id: "createdAt",
      header: () => <Header header="Created at" />,
      accessorFn: (row) => row.created_at,
      cell: ({ cell }) =>
        renderDateInfo(cell as Cell<WorkspaceServiceAccount, string>),
    },
    {
      id: "lastUsed",
      header: () => <Header header="Last used" />,
      accessorFn: (row) => row.last_used_at ?? "",
      cell: ({ cell }) =>
        renderDateInfo(cell as Cell<WorkspaceServiceAccount, string>),
    },
    {
      id: "actions",
      header: () => <Header />,
      cell: (cell) => {
        const apiKey = cell.row.original;
        return (
          <ApiKeyActions
            onDeleteClick={() => onDelete(apiKey)}
            onEditClick={() => onEdit(apiKey)}
          />
        );
      },
    },
  ];

  return columns;
};

export const ApiKeyOverview: React.FC<Props> = ({
  organizationId,
  workspaceId,
}) => {
  const { apiKeys } = useCapabilities();
  const { isLoading, data, isError, error } = useWorkspaceApiKeys(
    organizationId,
    workspaceId,
  );

  const [keyToEdit, setKeyToEdit] = useState<WorkspaceServiceAccount>();
  const [keyToDelete, setKeyToDelete] = useState<WorkspaceServiceAccount>();
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [createdApiKey, setCreatedApiKey] =
    useState<WorkspaceServiceAccountWithKey>();
  const columns = useMemo(
    () => buildColumns(setKeyToEdit, setKeyToDelete),
    [setKeyToEdit, setKeyToDelete],
  );

  const renderContent = () => {
    if (isError) {
      return (
        <EmptyState
          dataLoc="api-keys-error-state"
          description={errorMessage(error)}
          headline="Error loading API keys"
          icon={faTriangleExclamation}
          variant="error"
        />
      );
    }

    if (!isLoading && data?.length === 0) {
      return renderNoApiKeys();
    }

    return <Table columns={columns} data={data ?? []} isLoading={isLoading} />;
  };

  return (
    <DashboardContent
      Header={
        <SubHeader title="API keys">
          {apiKeys.canAccess && (
            <SubHeader.Button
              icon={faPlus}
              tooltip="Create API key"
              onClick={() => setApiKeyModalOpen(true)}
            />
          )}
        </SubHeader>
      }
    >
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          {renderContent()}
        </Card.Content>
      </Card>
      <EditApiKeyModal
        keyToEdit={keyToEdit}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={() => setKeyToEdit(undefined)}
      />
      <DeleteApiKeyModal
        keyToDelete={keyToDelete}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onClose={() => setKeyToDelete(undefined)}
      />
      <CreateApiKeyModal
        open={apiKeyModalOpen}
        organizationId={organizationId}
        workspaceId={workspaceId}
        onApiKeyCreate={(apiKey: WorkspaceServiceAccountWithKey) =>
          setCreatedApiKey(apiKey)
        }
        onClose={() => setApiKeyModalOpen(false)}
      />
      <ShowApiKeyModal
        createdApiKey={createdApiKey}
        onUnmount={() => setCreatedApiKey(undefined)}
      />
    </DashboardContent>
  );
};
